import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { API } from '../lib/api';
import VendorProfileForm from '../components/VendorProfileForm';
import VendorDangerZone from '../components/VendorDangerZone';
import VendorCertificatesPanel from '../components/VendorCertificatesPanel';
import VendorBillingPanel from '../components/VendorBillingPanel';
import VendorLoginCard from '../components/vendor/VendorLoginCard';
import VendorHeaderBar from '../components/vendor/VendorHeaderBar';
import VendorProductsTab from '../components/vendor/VendorProductsTab';
import VendorLeadsTab from '../components/vendor/VendorLeadsTab';
import VendorBuyLeadsTab from '../components/vendor/VendorBuyLeadsTab';
import VendorProductForm from '../components/vendor/VendorProductForm';
import { Package, MessageSquare, User, FileBadge, CreditCard, ClipboardList } from 'lucide-react';

/** Pick a product's image list: prefer `images` array, fall back to single `image`, else empty. */
const pickImages = (v) => {
  if (Array.isArray(v?.images) && v.images.length) return v.images;
  if (v?.image) return [v.image];
  return [];
};

const VendorDashboard = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [vendor, setVendor] = useState(null);
  const [products, setProducts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [buyLeads, setBuyLeads] = useState([]);
  const [tab, setTab] = useState('products');
  // Logged-in flag is derived from the cached vendor profile (httpOnly cookie is the
  // actual auth credential, not readable from JS). The flag flips false on any 401.
  const [isAuthed, setIsAuthed] = useState(() => !!localStorage.getItem('tgmart_vendor'));
  // Legacy localStorage token — only present for sessions started before the
  // httpOnly cookie migration. New logins no longer set this. Child panels still
  // pass it as an Authorization Bearer header for backwards compatibility.
  const token = typeof window !== 'undefined' ? (localStorage.getItem('tgmart_vendor_token') || '') : '';
  // Login state
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState(1); // 1=mobile, 2=otp, 'forgot'=otp+new-password
  const [loginTab, setLoginTab] = useState('otp'); // 'otp' | 'password'
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [devOtp, setDevOtp] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Edit modal
  const [editing, setEditing] = useState(null);
  // Google sign-in vendor profile completion state
  const [googleProfile, setGoogleProfile] = useState(null);
  const [gpSubmitting, setGpSubmitting] = useState(false);

  // axios is configured globally with withCredentials=true so the
  // httpOnly tgm_vendor_jwt cookie is sent automatically. We also pass it
  // explicitly here as a defensive belt-and-braces against import-order races.
  const authClient = axios.create({ baseURL: API, withCredentials: true });

  const loadAll = useCallback(async () => {
    try {
      const me = await authClient.get('/vendor/me');
      setVendor(me.data);
      localStorage.setItem('tgmart_vendor', JSON.stringify(me.data));
      window.dispatchEvent(new Event('tgmart-session-change'));
      const p = await authClient.get('/vendor/me/products');
      setProducts(p.data || []);
      const l = await authClient.get('/vendor/me/leads');
      setLeads(l.data || []);
      const bl = await authClient.get('/vendor/me/buy-leads');
      setBuyLeads(bl.data || []);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem('tgmart_vendor');
        window.dispatchEvent(new Event('tgmart-session-change'));
        setIsAuthed(false);
        setVendor(null);
      }
    }
    // authClient is created per render but only the cookie is needed for auth.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed]);

  useEffect(() => { if (isAuthed) loadAll(); }, [isAuthed, loadAll]);

  // Deep-link support: /vendor/dashboard?tab=… (from notification bell & recharge links).
  useEffect(() => {
    const t = new URLSearchParams(location.search).get('tab');
    if (['products', 'leads', 'buyleads', 'certificates', 'billing', 'profile'].includes(t)) setTab(t);
  }, [location.search]);

  const respondBuyLead = async (reqId, body) => {
    try {
      await authClient.post(`/vendor/me/buy-leads/${reqId}/respond`, body);
      toast({ title: 'Response sent', description: 'The buyer has been notified.' });
      const bl = await authClient.get('/vendor/me/buy-leads');
      setBuyLeads(bl.data || []);
      return true;
    } catch (err) {
      toast({ title: 'Failed to respond', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
      return false;
    }
  };

  const unlockBuyLead = async (reqId) => {
    try {
      const r = await authClient.post(`/vendor/me/buy-leads/${reqId}/unlock`);
      toast({ title: 'Lead unlocked', description: `Buyer contact revealed. Balance: ${r.data.balance} credits.` });
      const bl = await authClient.get('/vendor/me/buy-leads');
      setBuyLeads(bl.data || []);
      setVendor((v) => (v ? { ...v, lead_credits: r.data.balance } : v));
      return { ok: true };
    } catch (err) {
      if (err?.response?.status === 402) return { ok: false, insufficient: true };
      toast({ title: 'Failed to unlock', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
      return { ok: false };
    }
  };

  const sendOtp = async () => {
    if (!mobile || mobile.length < 10) return toast({ title: 'Valid mobile required', variant: 'destructive' });
    try {
      setSubmitting(true);
      const res = await axios.post(`${API}/auth/send-otp`, { mobile });
      setStep(2);
      if (res.data.dev_otp) {
        setDevOtp(res.data.dev_otp);
        toast({ title: 'OTP sent (dev)', description: `Dev OTP: ${res.data.dev_otp}` });
      } else toast({ title: 'OTP sent', description: `Sent to +91 ${mobile}` });
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail, variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const res = await axios.post(`${API}/vendor/auth/login`, { mobile, otp }, { withCredentials: true });
      if (res.data.vendor) localStorage.setItem('tgmart_vendor', JSON.stringify(res.data.vendor));
      window.dispatchEvent(new Event('tgmart-session-change'));
      setIsAuthed(true);
      toast({ title: 'Welcome back, ' + (res.data.vendor?.name || 'Vendor') });
    } catch (err) {
      toast({ title: 'Login failed', description: err?.response?.data?.detail, variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  const logout = async () => {
    try { await axios.post(`${API}/vendor/auth/logout`, {}, { withCredentials: true }); } catch (err) {
      console.warn('[Vendor] logout API failed; clearing local state:', err?.message || err);
    }
    localStorage.removeItem('tgmart_vendor_token');
    localStorage.removeItem('tgmart_vendor');
    window.dispatchEvent(new Event('tgmart-session-change'));
    setIsAuthed(false); setVendor(null); setProducts([]); setLeads([]); setBuyLeads([]); setStep(1); setMobile(''); setOtp(''); setDevOtp(null);
    setPassword(''); setNewPassword(''); setConfirmPassword(''); setLoginTab('otp');
  };

  const loginWithPassword = async (e) => {
    e?.preventDefault?.();
    if (!mobile || mobile.length !== 10) return toast({ title: 'Enter a valid 10-digit mobile number', variant: 'destructive' });
    if (!password) return toast({ title: 'Enter your password', variant: 'destructive' });
    try {
      setSubmitting(true);
      const res = await axios.post(`${API}/vendor/auth/login-password`, { mobile, password }, { withCredentials: true });
      if (res.data.vendor) localStorage.setItem('tgmart_vendor', JSON.stringify(res.data.vendor));
      window.dispatchEvent(new Event('tgmart-session-change'));
      setIsAuthed(true);
      toast({ title: 'Welcome back', description: res.data.vendor?.name || '' });
    } catch (err) {
      toast({ title: 'Login failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  const startForgotPassword = async () => {
    if (!mobile || mobile.length !== 10) return toast({ title: 'Enter a valid 10-digit mobile number first', variant: 'destructive' });
    try {
      setSubmitting(true);
      const res = await axios.post(`${API}/auth/send-otp`, { mobile });
      setStep('forgot'); setOtp(''); setNewPassword(''); setConfirmPassword('');
      if (res.data.dev_otp) { setDevOtp(res.data.dev_otp); toast({ title: 'OTP sent (dev)', description: `Dev OTP: ${res.data.dev_otp}` }); }
      else { toast({ title: 'OTP sent', description: `OTP sent to +91 ${mobile}` }); }
    } catch (err) {
      toast({ title: 'Failed to send OTP', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  const submitForgotPassword = async (e) => {
    e?.preventDefault?.();
    if (otp.length < 4) return toast({ title: 'Enter the OTP', variant: 'destructive' });
    if (newPassword.length < 8) return toast({ title: 'Password must be at least 8 characters', variant: 'destructive' });
    if (!/[A-Za-z]/.test(newPassword) || !/\d/.test(newPassword)) return toast({ title: 'Password must contain letters and numbers', variant: 'destructive' });
    if (newPassword !== confirmPassword) return toast({ title: 'Passwords do not match', variant: 'destructive' });
    try {
      setSubmitting(true);
      const res = await axios.post(`${API}/vendor/auth/forgot-password/reset`, { mobile, otp, new_password: newPassword }, { withCredentials: true });
      if (res.data.vendor) localStorage.setItem('tgmart_vendor', JSON.stringify(res.data.vendor));
      window.dispatchEvent(new Event('tgmart-session-change'));
      setIsAuthed(true);
      toast({ title: 'Password reset', description: 'You are now logged in.' });
    } catch (err) {
      toast({ title: 'Reset failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  const saveProduct = async (v) => {
    try {
      const images = pickImages(v);
      const payload = {
        name: v.name, slug: (v.slug || v.name || '').toLowerCase().replace(/\s+/g, '-').slice(0, 60),
        price: Number(v.price || 0), unit: v.unit || 'Piece', moq: v.moq || '1 Piece',
        image: images[0] || '', images,
        sub_category_id: v.sub_category_id || '', supplier_id: vendor.id,
        verified: true, gst: vendor.gst || '', description: v.description || '',
        specs: {},
      };
      if (editing.id) {
        await authClient.put(`/vendor/me/products/${editing.id}`, payload);
        toast({ title: 'Product updated' });
      } else {
        await authClient.post('/vendor/me/products', payload);
        toast({ title: 'Product added' });
      }
      setEditing(null);
      loadAll();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || err?.message, variant: 'destructive' });
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await authClient.delete(`/vendor/me/products/${id}`);
      toast({ title: 'Deleted' });
      loadAll();
    } catch (err) {
      toast({ title: 'Delete failed', variant: 'destructive' });
    }
  };

  const handleGoogleVendor = async (credential) => {
    try {
      setSubmitting(true);
      const res = await axios.post(`${API}/auth/google/vendor`, { credential });
      if (res.data.needs_profile) {
        setGoogleProfile({
          credential: res.data.google_credential || credential,
          email: res.data.email,
          company_name: res.data.suggested_name || '',
          contact_name: res.data.suggested_name || '',
          picture: res.data.picture || '',
          mobile: '', city: '', gst_number: '', year_established: '', business_type: '',
        });
        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
        toast({ title: 'Almost there', description: 'Fill in your business details to finish vendor signup.' });
        return;
      }
      if (res.data.pending) {
        toast({ title: 'Account not active', description: res.data.detail, variant: 'destructive' });
        return;
      }
      localStorage.setItem('tgmart_vendor', JSON.stringify(res.data.vendor || {}));
      window.dispatchEvent(new Event('tgmart-session-change'));
      setIsAuthed(true);
      toast({ title: 'Welcome back, ' + (res.data.vendor?.name || 'Vendor') });
    } catch (err) {
      toast({ title: 'Google sign-in failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  const submitGoogleProfile = async (e) => {
    e?.preventDefault?.();
    if (!googleProfile) return;
    if (!googleProfile.company_name?.trim()) return toast({ title: 'Company name is required', variant: 'destructive' });
    if (!/^[0-9]{10}$/.test(googleProfile.mobile)) return toast({ title: 'Valid 10-digit mobile required', variant: 'destructive' });
    if (!googleProfile.city?.trim()) return toast({ title: 'City is required', variant: 'destructive' });
    setGpSubmitting(true);
    try {
      const payload = {
        credential: googleProfile.credential,
        company_name: googleProfile.company_name.trim(),
        contact_name: googleProfile.contact_name?.trim() || googleProfile.company_name.trim(),
        mobile: googleProfile.mobile,
        city: googleProfile.city.trim(),
        gst_number: googleProfile.gst_number || '',
        year_established: googleProfile.year_established ? parseInt(googleProfile.year_established, 10) : 0,
        business_type: googleProfile.business_type || '',
        categories: [],
        description: googleProfile.description || '',
      };
      const res = await axios.post(`${API}/vendor/google/finalize`, payload);
      toast({
        title: 'Vendor registration submitted',
        description: res.data.message || 'Awaiting admin activation. We will email you once approved.',
      });
      setGoogleProfile(null);
    } catch (err) {
      toast({ title: 'Registration failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setGpSubmitting(false); }
  };

  if (!isAuthed || !vendor) {
    let customerSession = null;
    try {
      const raw = localStorage.getItem('tgmart_user');
      if (localStorage.getItem('tgmart_token') && raw) customerSession = JSON.parse(raw);
    } catch (err) { console.warn('VendorDashboard: failed to read customer session', err); }

    const signOutCustomer = () => {
      localStorage.removeItem('tgmart_token');
      localStorage.removeItem('tgmart_user');
      window.dispatchEvent(new Event('tgmart-session-change'));
      window.location.reload();
    };

    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <VendorLoginCard
          customerSession={customerSession}
          onSignOutCustomer={signOutCustomer}
          googleProfile={googleProfile}
          setGoogleProfile={setGoogleProfile}
          submitGoogleProfile={submitGoogleProfile}
          gpSubmitting={gpSubmitting}
          handleGoogleVendor={handleGoogleVendor}
          step={step}
          setStep={setStep}
          loginTab={loginTab}
          setLoginTab={setLoginTab}
          mobile={mobile}
          setMobile={setMobile}
          otp={otp}
          setOtp={setOtp}
          password={password}
          setPassword={setPassword}
          newPassword={newPassword}
          setNewPassword={setNewPassword}
          confirmPassword={confirmPassword}
          setConfirmPassword={setConfirmPassword}
          devOtp={devOtp}
          submitting={submitting}
          sendOtp={sendOtp}
          verifyOtp={verifyOtp}
          loginWithPassword={loginWithPassword}
          startForgotPassword={startForgotPassword}
          submitForgotPassword={submitForgotPassword}
        />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <VendorHeaderBar vendor={vendor} onLogout={logout} />
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          {/* Horizontal scroller so the 5 tabs never wrap/overlap on mobile.
              Mirrors the same fix applied to SupplierProfilePage. */}
          <div className="-mx-4 px-4 overflow-x-auto scrollbar-hide">
            <TabsList className="inline-flex w-max flex-nowrap bg-white border border-gray-200 p-1 h-auto">
              <TabsTrigger value="products" className="data-[state=active]:bg-[#ee5e23] data-[state=active]:text-white px-3 sm:px-4 py-2 whitespace-nowrap text-sm"><Package className="w-4 h-4 mr-1.5 sm:mr-2" /> My Products ({products.length})</TabsTrigger>
              <TabsTrigger value="leads" className="data-[state=active]:bg-[#ee5e23] data-[state=active]:text-white px-3 sm:px-4 py-2 whitespace-nowrap text-sm"><MessageSquare className="w-4 h-4 mr-1.5 sm:mr-2" /> Leads ({leads.length})</TabsTrigger>
              <TabsTrigger value="buyleads" className="data-[state=active]:bg-[#ee5e23] data-[state=active]:text-white px-3 sm:px-4 py-2 whitespace-nowrap text-sm" data-testid="vendor-tab-buyleads"><ClipboardList className="w-4 h-4 mr-1.5 sm:mr-2" /> Buy Leads ({buyLeads.length})</TabsTrigger>
              <TabsTrigger value="certificates" className="data-[state=active]:bg-[#ee5e23] data-[state=active]:text-white px-3 sm:px-4 py-2 whitespace-nowrap text-sm" data-testid="vendor-tab-certificates"><FileBadge className="w-4 h-4 mr-1.5 sm:mr-2" /> Certificates</TabsTrigger>
              <TabsTrigger value="billing" className="data-[state=active]:bg-[#ee5e23] data-[state=active]:text-white px-3 sm:px-4 py-2 whitespace-nowrap text-sm" data-testid="vendor-tab-billing"><CreditCard className="w-4 h-4 mr-1.5 sm:mr-2" /> Billing</TabsTrigger>
              <TabsTrigger value="profile" className="data-[state=active]:bg-[#ee5e23] data-[state=active]:text-white px-3 sm:px-4 py-2 whitespace-nowrap text-sm" data-testid="vendor-tab-profile"><User className="w-4 h-4 mr-1.5 sm:mr-2" /> Profile</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="products" className="mt-4">
            <VendorProductsTab
              vendor={vendor}
              products={products}
              onAdd={() => setEditing({})}
              onEdit={setEditing}
              onDelete={deleteProduct}
            />
          </TabsContent>

          <TabsContent value="leads" className="mt-4">
            <VendorLeadsTab leads={leads} />
          </TabsContent>

          <TabsContent value="buyleads" className="mt-4">
            <VendorBuyLeadsTab
              buyLeads={buyLeads}
              onRespond={respondBuyLead}
              onUnlock={unlockBuyLead}
              credits={Number(vendor?.lead_credits) || 0}
              onRecharge={() => setTab('billing')}
            />
          </TabsContent>

          <TabsContent value="profile" className="mt-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold">Business Profile</h2>
              <span className="text-xs text-gray-500">Keep your storefront information up to date.</span>
            </div>
            <VendorProfileForm
              vendor={vendor}
              apiBase={process.env.REACT_APP_BACKEND_URL}
              token={token}
              onSaved={(updated) => setVendor(updated)}
            />
            <VendorDangerZone />
          </TabsContent>

          <TabsContent value="certificates" className="mt-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold">Business Certificates</h2>
              <span className="text-xs text-gray-500">Verified certificates appear publicly on your profile.</span>
            </div>
            <VendorCertificatesPanel token={token} />
          </TabsContent>

          <TabsContent value="billing" className="mt-4">
            <VendorBillingPanel token={token} onPlanUpdated={loadAll} />
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing?.id ? 'Edit' : 'Add'} Product</DialogTitle></DialogHeader>
          {editing && <VendorProductForm initial={editing} onSave={saveProduct} onCancel={() => setEditing(null)} token={token} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorDashboard;
